import React, { useState } from 'react'
import { isEmpty } from 'lodash'
import { IconClock, IconInfoCircle, IconSparkles } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Toggle } from '@/components/forms/Checkbox'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { getOpportunityLabel, splitRecommendationIntoDisplayItems } from './AgenticInsights.helpers'
import { SCOPE_LOADING } from './AgenticInsights.constants'
import { ExperimentScope } from './ExperimentScope'

const formatBlockingExperiments = (titles) => {
  if (isEmpty(titles)) {
    return 'another experiment'
  }
  if (titles.length === 1) {
    return `“${titles[0]}”`
  }
  return `${titles.length} other experiments`
}

const formatBlockedUntil = (isoDate) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })

// What the agent will be told, shown before launch and after. This is the real
// text: the same per-sentence split that gets written into the playbook, so it's
// a preview rather than an illustration of one.
const WhatAgentsGet = ({ insight }) => {
  const recommendationSteps = splitRecommendationIntoDisplayItems(insight.recommendation)

  return (
    <div className="delivery-options__getbox">
      <div className="delivery-options__getbox-label">
        <IconSparkles className="icon-svg" /> What agents will get
      </div>
      {isEmpty(recommendationSteps) ? (
        <p className="delivery-options__getbox-text">{insight.recommendation}</p>
      ) : (
        <ul className="delivery-options__getbox-list">
          {recommendationSteps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ul>
      )}
      <p className="delivery-options__detect">
        You don&apos;t write a trigger — we automatically detect the calls where this objection
        comes up.
      </p>
    </div>
  )
}

// Choose-at-launch delivery. Both interventions default on (we recommend both);
// the user can deselect before launching. Not editable after launch — this
// block is only shown for a not-yet-launched insight.
//
// `coachingConflicts` comes from the pre-flight check: an agent can only be
// coached on one thing at a time, so a running experiment holds its agents and
// this insight can't be launched until it finishes.
//
// When that happens the delivery controls are replaced rather than disabled.
// Greying the coaching toggle while leaving the guidance toggle live advertised a
// guidance-only launch, which doesn't exist — an experiment tests both together,
// so a one-sided version isn't a result worth measuring.
export const DeliveryOptions = ({ insight, launching, scope, playbooks, onLaunch }) => {
  const [realtime, setRealtime] = useState(true)
  const [coaching, setCoaching] = useState(true)
  // SCOPE_LOADING means the request hasn't settled; null means it failed. Launch
  // waits for the first because it needs the playbooks, and proceeds on the second
  // because the run's configured playbooks are a workable fallback.
  const loadingScope = scope === SCOPE_LOADING
  const blockedAgents = loadingScope ? 0 : scope?.conflictingAgents ?? 0
  const blocked = blockedAgents > 0
  const agents = loadingScope ? [] : scope?.agents ?? []

  // Controls are replaced rather than disabled, as for a coaching conflict
  // below — and more so here, since this is permanent for the insight rather
  // than a wait, so there is nothing a disabled control could later enable.
  if (!isEmpty(insight.experimentBlockedReason)) {
    return (
      <div className="delivery-options">
        <WhatAgentsGet insight={insight} />
        <div className="delivery-options__blocked">
          <div className="delivery-options__blocked-head">Can&apos;t measure this one</div>
          <p>{insight.experimentBlockedReason}</p>
        </div>
      </div>
    )
  }

  if (blocked) {
    return (
      <div className="delivery-options">
        <WhatAgentsGet insight={insight} />
        {/* Still shown while blocked: "who would this have reached" is exactly what
            you want to know when you can't launch yet. Only the controls go. */}
        <ExperimentScope
          insight={insight}
          playbooks={playbooks}
          playbookCids={scope.guidancePlaybookCids}
          coachedAgents={agents}
        />
        <div className="delivery-options__blocked">
          <div className="delivery-options__blocked-head">Can&apos;t launch yet</div>
          <p>
            An experiment delivers real-time guidance and post-call coaching together.{' '}
            {blockedAgents} of these {scope.totalAgents} agents are already being coached by{' '}
            {formatBlockingExperiments(scope.blockingExperiments)}
            {scope.blockedUntil ? `, which finishes ${formatBlockedUntil(scope.blockedUntil)}` : ''}
            . You&apos;ll be able to launch this then.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="delivery-options">
      <WhatAgentsGet insight={insight} />

      <ExperimentScope
        insight={insight}
        playbooks={playbooks}
        playbookCids={loadingScope ? [] : scope?.guidancePlaybookCids ?? []}
        coachedAgents={agents}
      />

      <div className="delivery-options__how">
        How to deliver it
        <InteractiveTooltip content="We strongly recommend both. Agents get guidance in the moment during the call, then it's reinforced with coaching afterward.">
          <IconInfoCircle className="icon-svg" />
        </InteractiveTooltip>
      </div>

      <div className="delivery-options__row">
        <Toggle
          aria-label="Real-time guidance"
          checked={realtime}
          onChange={() => setRealtime((current) => !current)}
        />
        <span className="delivery-options__row-text">
          <span className="delivery-options__row-title">Real-time guidance</span>
          <span className="delivery-options__row-desc">
            A prompt appears during live calls where this comes up.
          </span>
        </span>
      </div>

      <div className="delivery-options__row">
        <Toggle
          aria-label="Post-call coaching"
          checked={coaching}
          onChange={() => setCoaching((current) => !current)}
        />
        <span className="delivery-options__row-text">
          <span className="delivery-options__row-title">Post-call coaching</span>
          <span className="delivery-options__row-desc">
            Agents get a coaching tip after relevant calls. Only one coaching focus can be live per
            agent at a time.
          </span>
        </span>
      </div>

      <p className="delivery-options__measure">
        <IconClock className="icon-svg" /> Starts a 30-day measurement in{' '}
        <b>{getOpportunityLabel(insight)}</b> against your recent baseline.
      </p>

      <Button
        brand
        fullWidth
        // Waits for the scope, because that request carries the playbooks the
        // guidance attaches to. Launching without them creates the coaching and
        // then reports the guidance as failed — the exact half-launch this panel
        // exists to prevent.
        loading={launching || loadingScope}
        disabled={!realtime && !coaching}
        onClick={() => onLaunch({ realtime, coaching })}
      >
        Launch experiment
      </Button>
    </div>
  )
}
